import { colors } from "./colors";
import { assetsRoot } from "config";
// Mock product data for the poppi variety-pack PDPs. Each product is themed by
// two colors (primary = background, secondary = text/accent/waves) so a single
// PDP component renders both pages with different palettes.

const A = `${assetsRoot}/poppi/pdp`;
const SA = `${assetsRoot}/poppi/sour-apple`;
const G = `${assetsRoot}/poppi/pdp-gallery`;
const FALL_ASSET_ROOT = `${assetsRoot}/poppi/fall-for-flavor-assets`;

export const FALL_FOR_FLAVOR_CARD_IMAGE = `${FALL_ASSET_ROOT}/0724_2026WEBFallforFlavorPDPAssets.png`;

const FALL_FOR_FLAVOR_GALLERY = [
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_1_HERO.jpg`,
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_2.jpg`,
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_3.jpg`,
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_4_STICKERS.jpg`,
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_5.jpg`,
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_6.jpg`,
  `${FALL_ASSET_ROOT}/0724_2026_WEB_Fall_for_Flavor_PDP_Assets_8_RENDER.jpg`,
];

export interface PdpFlavor {
  name: string;
  icon: string;
  color: string;
  qty: number;
}

export interface PdpNutrition {
  flavor: string;
  calories: number;
  totalFat: string;
  sodium: string;
  carbs: string;
  carbsPct: string;
  fiber: string;
  fiberPct: string;
  sugars: string;
  addedSugars: string;
  addedSugarsPct: string;
  protein: string;
  ingredients: string;
  // trailing lines under the ingredients ("*Concentrate", "Contains N% Juice")
  notes: string[];
}

export interface PdpReview {
  title: string;
  text: string;
  author: string;
}

export interface PdpProduct {
  handle: string;
  // SmartCommerce/click2cart UPC — scraped verbatim from live's data-sc-upc
  // attributes (drinkpoppi.com PDP fixed add-to-cart bars, 2026-08-25);
  // absent on PDPs retired from prod, which ship no smart button on live
  upc?: string;
  name: string;
  description: string;
  packSize: string;
  primary: string; // theme background
  secondary: string; // theme text / accent / waves
  gallery: string[];
  // collection and cross-sell card art when it differs from gallery image 1
  cardImage?: string;
  // single-flavor renders letterbox better than the pack photography
  galleryFit?: "cover" | "contain";
  // live gallery filmstrip: slides are natural-width photos; a tuple renders
  // two square photos stacked in one slide (like live's sticker slide)
  galleryStrip?: (string | [string, string])[];
  // external "buy now" destination (live links the pack to its Amazon listing)
  buyUrl?: string;
  // "Taste the Obsession" filmstrip tiles (240x400 stills from the live page)
  tasteTiles?: string[];
  // live's Firework playlist for this product (verbatim captions; mp4 +
  // thumbnail served straight from the Firework CDN like live)
  tasteVideos?: { caption: string; src: string; thumb: string }[];
  flavors: PdpFlavor[];
  nutrition: PdpNutrition[];
  reviews: PdpReview[];
  // live shows a 4th "Natural Caffeine" icon badge on the caffeinated singles
  caffeineBadge?: boolean;
  // live has no "Taste the Obsession" section on this product
  hideTaste?: boolean;
}

// Verified per-flavor nutrition — extracted verbatim from drinkpoppi.com PDPs
// (2026-08-19) and independently re-verified against the rendered pages.
// FDA-REGULATED LABEL CONTENT: calories, caffeine, % juice and ingredient
// declarations. Do not edit without the authoritative product data source
// and regulatory sign-off.
const NUTRITION: Record<string, PdpNutrition> = {
  "Alpine Blast": {
    flavor: "Alpine Blast", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Organic Apple Cider Vinegar, Natural Flavors, Apple Juice*, Lime Juice*, Organic Agave Inulin, Citric Acid, Stevia Leaf Extract, Acacia, Vegetable Juice & Turmeric for Color, Natural Caffeine, Citrus Pectin.",
    notes: ["*Concentrate", "Contains 3% Juice", "Contains 55mg Natural Caffeine"],
  },
  "Cherry Cola": {
    flavor: "Cherry Cola", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "8g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Natural Flavors, Apple Juice Concentrate, Organic Apple Cider Vinegar, Organic Agave Inulin, Natural Tartaric Acid, Stevia Leaf Extract, Natural Caffeine.",
    notes: ["Contains 3% Juice", "Contains 40mg Natural Caffeine"],
  },
  "Cherry Limeade": {
    flavor: "Cherry Limeade", calories: 35, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Organic Apple Cider Vinegar, Cherry Juice*, Natural Flavors, Lemon Juice*, Lime Juice*, Organic Agave Inulin, Stevia Leaf Extract, Vegetable Juice (color).",
    notes: ["*Concentrate", "Contains 3% Juice"],
  },
  "Classic Cola": {
    flavor: "Classic Cola", calories: 35, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Natural Flavors, Apple Juice Concentrate, Organic Apple Cider Vinegar, Organic Agave Inulin, Stevia Leaf Extract, Natural Tartaric Acid, Natural Caffeine.",
    notes: ["Contains 3% Juice", "Contains 40mg Natural Caffeine"],
  },
  "Cream Soda": {
    flavor: "Cream Soda", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, White Grape Juice Concentrate, Natural Flavors, Organic Apple Cider Vinegar, Organic Agave Inulin, Natural Lactic Acid, Stevia Leaf Extract, Natural Tartaric Acid.",
    notes: ["Contains 2% Juice"],
  },
  "Doc Pop": {
    flavor: "Doc Pop", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Natural Flavors, Apple Juice Concentrate, Organic Apple Cider Vinegar, Organic Agave Inulin, Stevia Leaf Extract, Tartaric Acid, Natural Caffeine.",
    notes: ["Contains 3% Juice", "Contains 40mg Natural Caffeine"],
  },
  "Ginger Lime": {
    flavor: "Ginger Lime", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Organic Apple Cider Vinegar, Lime Juice*, Natural Flavors, Lemon Juice*, Organic Agave Inulin, Stevia Leaf Extract, Ginger Extract.",
    notes: ["*Concentrate", "Contains 5% Juice"],
  },
  "Grape": {
    flavor: "Grape", calories: 30, totalFat: "0g", sodium: "5mg",
    carbs: "10g", carbsPct: "4%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "2g", addedSugarsPct: "4%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, White Grape Juice*, Natural Flavors, Organic Cane Sugar, Organic Apple Cider Vinegar, Apple Juice*, Fruit and Vegetable Juice (color), Organic Agave Inulin, Citric Acid, Stevia Leaf Extract.",
    notes: ["*Concentrate", "Contains 5% Juice"],
  },
  "Lemon Lime": {
    flavor: "Lemon Lime", calories: 35, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Apple Juice*, Organic Apple Cider Vinegar, Natural Flavors, Citric Acid, Organic Agave Inulin, Stevia Leaf Extract, Lime Juice*, Lemon Juice*.",
    notes: ["*Concentrate", "Contains 4% Juice"],
  },
  "Orange": {
    flavor: "Orange", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Apple Juice*, Organic Cane Sugar, Organic Apple Cider Vinegar, Natural Flavors, Citric Acid, Orange Juice*, Organic Agave Inulin, Stevia Leaf Extract, Beta Carotene (color).",
    notes: ["*Concentrate", "Contains 5% Juice"],
  },
  "Orange Cream": {
    flavor: "Orange Cream", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Apple Juice*, Organic Cane Sugar, Organic Apple Cider Vinegar, Natural Flavors, Citric Acid, Organic Agave Inulin, Stevia Leaf Extract, Orange Juice*, Beta Carotene (color).",
    notes: ["*Concentrate", "Contains 5% Juice"],
  },
  "Raspberry Rose": {
    flavor: "Raspberry Rose", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Raspberry Juice*, Organic Apple Cider Vinegar, Natural Flavors, Lemon Juice*, Organic Agave Inulin, Stevia Leaf Extract, Vegetable Juice (color).",
    notes: ["*Concentrate", "Contains 5% juice"],
  },
  "Root Beer": {
    flavor: "Root Beer", calories: 30, totalFat: "0g", sodium: "5mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Natural Flavors, Apple Juice Concentrate, Organic Apple Cider Vinegar, Organic Agave Inulin, Stevia Leaf Extract, Natural Tartaric Acid.",
    notes: ["Contains 3% Juice"],
  },
  "Shirley Temple": {
    flavor: "Shirley Temple", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Apple Juice Concentrate, Natural Flavors, Vegetable Juice (color), Organic Apple Cider Vinegar, Citric Acid, Organic Agave Inulin, Stevia Leaf Extract.",
    notes: ["Contains 3% Juice"],
  },
  "Sour Apple": {
    flavor: "Sour Apple", calories: 35, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Apple Juice Concentrate, Apple Cider Vinegar, Natural Flavors, Citric Acid, Tartaric Acid, Organic Agave Inulin, Stevia Leaf Extract.",
    notes: ["Contains 3% Juice"],
  },
  "Strawberry Lemon": {
    flavor: "Strawberry Lemon", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Organic Apple Cider Vinegar, Natural Flavors, Lemon Juice*, Strawberry Juice*, Organic Agave Inulin, Stevia Leaf Extract, Vegetable Juice (color).",
    notes: ["*Concentrate", "Contains 6% Juice"],
  },
  "Watermelon": {
    flavor: "Watermelon", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Apple Juice*, Organic Apple Cider Vinegar, Natural Flavors, Organic Agave Inulin, Stevia Leaf Extract, Tartaric Acid, Vegetable Juice (color), Watermelon Juice*.",
    notes: ["*Concentrate", "Contains 3% Juice"],
  },
  "Wild Berry": {
    flavor: "Wild Berry", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5", addedSugars: "3", addedSugarsPct: "6%", protein: "0g",
    ingredients:
      "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Organic Apple Cider Vinegar, Apple Juice*, Citric Acid, Natural Flavors, Raspberry Juice*, Organic Agave Inulin, Vegetable Juice (color), Stevia Leaf Extract.",
    notes: ["*Concentrate", "Contains 4% Juice"],
  },
  "Punch Pop": {
    flavor: "Punch Pop", calories: 30, totalFat: "0g", sodium: "0mg",
    carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%",
    sugars: "5g", addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
    ingredients:
      "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Apple Juice Concentrate, Citric Acid, Natural Flavors, Organic Apple Cider Vinegar, Vegetable Juice Color, Organic Agave Inulin, Stevia Leaf Extract.",
    notes: ["Contains 3% Juice"],
  },
};

const nutritionFor = (flavor: string): PdpNutrition => {
  const n = NUTRITION[flavor];
  if (!n) throw new Error(`no verified nutrition record for flavor: ${flavor}`);
  return n;
};

const REVIEWS: PdpReview[] = [
  {
    title: "This is the Winner!",
    text: "An absolute winner and by far one of my very favorite variety packs they've ever released. The taste is spot on — if you like the originals, you'll love this.",
    author: "Jamie R.",
  },
  {
    title: "Tastes like my old town soda fountain",
    text: "Bubbly, nostalgic, and not too sweet. I keep a fridge fully stocked and my whole family is obsessed. Feels good to drink something this fun.",
    author: "Marcus T.",
  },
  {
    title: "Obsessed is an understatement",
    text: "The flavor is unreal and I love that it's actually good for me. 12 cans never lasts long in this house!",
    author: "Priya S.",
  },
];

export const PDP_PRODUCTS: Record<string, PdpProduct> = {
  "fall-for-flavor": {
    handle: "fall-for-flavor",
    upc: "810063713140",
    name: "Fall for Flavor",
    description:
      "The leaves are falling… and so will you! Fall head over heels for this refreshing mix of fruity favorites and iconic sips.",
    packSize: "12 Pack",
    primary: "#cafe26",
    secondary: "#ff7500",
    cardImage: FALL_FOR_FLAVOR_CARD_IMAGE,
    gallery: [...FALL_FOR_FLAVOR_GALLERY],
    galleryStrip: [
      FALL_FOR_FLAVOR_GALLERY[0],
      FALL_FOR_FLAVOR_GALLERY[1],
      [FALL_FOR_FLAVOR_GALLERY[2], FALL_FOR_FLAVOR_GALLERY[3]],
      FALL_FOR_FLAVOR_GALLERY[4],
      FALL_FOR_FLAVOR_GALLERY[5],
      FALL_FOR_FLAVOR_GALLERY[6],
    ],
    flavors: [
      { name: "Sour Apple", icon: `${SA}/icon.jpg`, color: "#bcff00", qty: 3 },
      { name: "Shirley Temple", icon: `${A}/flavor-icons/shirley-temple.png`, color: "#f26b6b", qty: 3 },
      { name: "Root Beer", icon: `${A}/flavor-icons/root-beer.png`, color: "#7c2e3d", qty: 3 },
      { name: "Orange", icon: `${A}/flavor-icons/orange.png`, color: colors.orange, qty: 3 },
    ],
    nutrition: ["Sour Apple", "Shirley Temple", "Root Beer", "Orange"].map(nutritionFor),
    hideTaste: true,
    reviews: [],
  },
  "major-mix": {
    handle: "major-mix",
    upc: "810063712587",
    name: "Major Mix",
    description:
      "This variety pack is officially legends only. With a mix of some of our most major flavors, get ready to bow in the presence of greatness.",
    packSize: "12 Pack",
    primary: "#8e0011",
    secondary: "#f26b6b",
    gallery: [
      `${A}/live/major-mix-01.webp`,
      `${A}/live/major-mix-02.webp`,
      `${A}/live/major-mix-03.webp`,
      `${A}/live/major-mix-04.webp`,
      `${A}/live/major-mix-05.webp`,
      `${A}/live/major-mix-06.webp`,
      `${G}/major-mix-07.jpg`,
      `${G}/major-mix-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/major-mix-01.webp`,
      `${A}/live/major-mix-02.webp`,
      [`${A}/live/major-mix-03.webp`, `${A}/live/major-mix-04.webp`],
      `${A}/live/major-mix-05.webp`,
      `${A}/live/major-mix-06.webp`,
      `${G}/major-mix-07.jpg`,
      `${G}/major-mix-08.jpg`,
    ],
    flavors: [
      { name: "Shirley Temple", icon: `${A}/flavor-icons/shirley-temple.png`, color: "#f26b6b", qty: 2 },
      { name: "Cherry Cola", icon: `${A}/flavor-icons/cherry-cola.png`, color: "#700058", qty: 2 },
      { name: "Root Beer", icon: `${A}/flavor-icons/root-beer.png`, color: "#7c2e3d", qty: 2 },
      { name: "Lemon Lime", icon: `${A}/flavor-icons/lemon-lime.png`, color: "#137c21", qty: 2 },
      { name: "Cream Soda", icon: `${A}/flavor-icons/cream-soda.jpg`, color: "#0667be", qty: 2 },
      { name: "Orange Cream", icon: `${A}/flavor-icons/orange-cream.png`, color: "#ffe6d1", qty: 2 },
    ],
    nutrition: ["Shirley Temple", "Cherry Cola", "Root Beer", "Lemon Lime", "Cream Soda", "Orange Cream"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5qkyeb
    tasteVideos: [
      {
        caption: "a new star just arrived on the scene 🍒✨🎥",
        src: "https://cdn4.fireworktv.com/medias/2026/1/6/1767737492-toywuzcs/transcoded/720/SHIRLEY20LAUNCH20UGC.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2026/1/6/1767737492-toywuzcs/transcoded/720/SHIRLEY20LAUNCH20UGC.jpg",
      },
      {
        caption: "made for each other 💕",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-uybgmiox/transcoded/720/creamsoda-float.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-uybgmiox/transcoded/720/creamsoda-float.jpg",
      },
      {
        caption: "the perfect spritz ✨",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-wqtlcfnm/transcoded/720/OrangeCreamSpritz.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-wqtlcfnm/transcoded/720/OrangeCreamSpritz.jpg",
      },
      {
        caption: "r u refreshed yet? 😏",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ltbzxrow/transcoded/720/LemonLimePour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ltbzxrow/transcoded/720/LemonLimePour.jpg",
      },
      {
        caption: "Can We Talk About Cherry Cola?",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-cisrjgea/transcoded/720/allisontalkingaboutCherryCola.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-cisrjgea/transcoded/720/allisontalkingaboutCherryCola.jpg",
      },
      {
        caption: "delicious... and gorgeous",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-syizjhlq/transcoded/720/OOHaesthetic--teaser.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-syizjhlq/transcoded/720/OOHaesthetic--teaser.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "This is the Winner!",
        text: "poppi Shirley Temple is an absolute winner and by far one of my very favorite flavors they’ve ever released. The taste is spot on, and if you like the original drink, you’ll love this. I’m so excited to finally have this flavor in my lineup.",
        author: "EE",
      },
      {
        title: "Tastes like it came from my old town soda fountain!",
        text: "The Root Beer, Cream Soda, Orange Cream, and Cherry Cola are our favorite flavors!",
        author: "EdZDG",
      },
    ],
  },
  "functional-favorites": {
    handle: "functional-favorites",
    upc: "167386826",
    name: "Fun Favorites",
    description:
      "Can’t decide on a flavor? We get it. This variety pack of refreshing and fruity faves means you never have to choose.",
    packSize: "12 Pack",
    primary: colors.yellow,
    secondary: colors.magenta,
    gallery: [
      `${A}/live/functional-favorites-01.webp`,
      `${A}/live/functional-favorites-02.webp`,
      `${A}/live/functional-favorites-03.webp`,
      `${A}/live/functional-favorites-04.webp`,
      `${A}/live/functional-favorites-05.webp`,
      `${A}/live/functional-favorites-06.webp`,
      `${G}/functional-favorites-07.jpg`,
      `${G}/functional-favorites-08.jpg`,
      `${G}/functional-favorites-09.jpg`,
      `${G}/functional-favorites-10.jpg`,
    ],
    galleryStrip: [
      `${A}/live/functional-favorites-01.webp`,
      `${A}/live/functional-favorites-02.webp`,
      [`${A}/live/functional-favorites-03.webp`, `${A}/live/functional-favorites-04.webp`],
      `${A}/live/functional-favorites-05.webp`,
      `${A}/live/functional-favorites-06.webp`,
      `${G}/functional-favorites-07.jpg`,
      [`${G}/functional-favorites-08.jpg`, `${G}/functional-favorites-09.jpg`],
      `${G}/functional-favorites-10.jpg`,
    ],
    flavors: [
      { name: "Orange", icon: `${A}/flavor-icons/orange.png`, color: colors.orange, qty: 2 },
      { name: "Watermelon", icon: `${A}/flavor-icons/watermelon.png`, color: "#6bff4f", qty: 2 },
      { name: "Raspberry Rose", icon: `${A}/flavor-icons/raspberry-rose.png`, color: colors.pink, qty: 2 },
      { name: "Ginger Lime", icon: `${A}/flavor-icons/ginger-lemon.png`, color: colors.lime, qty: 2 },
      { name: "Strawberry Lemon", icon: `${A}/flavor-icons/strawberry-lemon.png`, color: colors.magenta, qty: 2 },
      { name: "Cherry Limeade", icon: `${A}/flavor-icons/cherry-limeade.png`, color: "#d10119", qty: 2 },
    ],
    nutrition: ["Strawberry Lemon", "Ginger Lime", "Orange", "Watermelon", "Cherry Limeade", "Raspberry Rose"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist o3rWY5
    tasteVideos: [
      {
        caption: "watermelon without the seeds 🍉",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-kmrqenzp/transcoded/720/watermelonevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-kmrqenzp/transcoded/720/watermelonevergreen.jpg",
      },
      {
        caption: "she's flawless 🍒🍋‍🟩",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-yliebrzk/transcoded/720/aestheticcherrylimeadevideos.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-yliebrzk/transcoded/720/aestheticcherrylimeadevideos.jpg",
      },
      {
        caption: "Raspberry Rose Sweet Treat",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fqukhleo/transcoded/720/rasroseicecream.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fqukhleo/transcoded/720/rasroseicecream.jpg",
      },
      {
        caption: "Orange You Obsessed Yet?",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-cwxrgmzp/transcoded/720/quickorangevideo.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-cwxrgmzp/transcoded/720/quickorangevideo.jpg",
      },
      {
        caption: "we 💚 Ginger Lime",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-ibxgtyes/transcoded/720/gingerlimeevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-ibxgtyes/transcoded/720/gingerlimeevergreen.jpg",
      },
      {
        caption: "your everyday pick me up 🍓🍋",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-sqjtudhe/transcoded/720/strawberrylemonwfruit.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-sqjtudhe/transcoded/720/strawberrylemonwfruit.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "One of the best!",
        text: "I love Poppi products. Raspberry Rose, Watermelon and Orange are my favorites! I love prebiotics, the fruity flavors and minimal sugar! Keep them coming!",
        author: "Shelly424",
      },
      {
        title: "Refreshing taste of Ginger Lime",
        text: "I now have a new favorite poppi - Ginger Lime. Serving it up over ice gives it the best burst of flavors for your taste buds. Sorry Raspberry Rose and Cherry Limeade, but I still love you too.",
        author: "Di03",
      },
      {
        title: "Great taste",
        text: "Soo good great replacement for soda. The strawberry lemon, ginger lime, and cherry limeade are my favorite",
        author: "Deeb",
      },
    ],
  },
  "crowd-pleasers": {
    handle: "crowd-pleasers",
    upc: "810063711238",
    name: "Crowd Pleasers",
    description:
      "The crowd has voted, and this combo of flavors has been proven to be pleasing. With a little something for everyone, there’s no way to go wrong.",
    packSize: "12 Pack",
    primary: colors.purple,
    secondary: colors.cyan,
    gallery: [
      `${A}/live/crowd-pleasers-01.webp`,
      `${A}/live/crowd-pleasers-02.webp`,
      `${A}/live/crowd-pleasers-03.webp`,
      `${A}/live/crowd-pleasers-04.webp`,
      `${A}/live/crowd-pleasers-05.webp`,
      `${A}/live/lemon-lime-06.webp`,
      `${G}/crowd-pleasers-07.jpg`,
      `${G}/crowd-pleasers-08.jpg`,
      `${G}/crowd-pleasers-09.jpg`,
      `${G}/crowd-pleasers-10.jpg`,
    ],
    galleryStrip: [
      `${A}/live/crowd-pleasers-01.webp`,
      `${A}/live/crowd-pleasers-02.webp`,
      [`${A}/live/crowd-pleasers-03.webp`, `${A}/live/crowd-pleasers-04.webp`],
      `${A}/live/crowd-pleasers-05.webp`,
      `${A}/live/lemon-lime-06.webp`,
      `${G}/crowd-pleasers-07.jpg`,
      [`${G}/crowd-pleasers-08.jpg`, `${G}/crowd-pleasers-09.jpg`],
      `${G}/crowd-pleasers-10.jpg`,
    ],
    flavors: [
      { name: "Doc Pop", icon: `${A}/flavor-icons/doc-pop.png`, color: "#f66ff8", qty: 2 },
      { name: "Wild Berry", icon: `${A}/flavor-icons/wild-berry.png`, color: colors.cyan, qty: 2 },
      { name: "Lemon Lime", icon: `${A}/flavor-icons/lemon-lime.png`, color: "#137c21", qty: 2 },
      { name: "Cherry Cola", icon: `${A}/flavor-icons/cherry-cola.png`, color: "#700058", qty: 2 },
      { name: "Grape", icon: `${A}/flavor-icons/grape.png`, color: "#8725c3", qty: 2 },
      { name: "Orange Cream", icon: `${A}/flavor-icons/orange-cream.png`, color: "#ffe6d1", qty: 2 },
    ],
    nutrition: ["Doc Pop", "Lemon Lime", "Wild Berry", "Orange Cream", "Grape", "Cherry Cola"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist oQB3rb
    tasteVideos: [
      {
        caption: "we have a crush... on Lemon Lime 🤭",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ivndcxjs/transcoded/720/lemonlime-whimsical.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ivndcxjs/transcoded/720/lemonlime-whimsical.jpg",
      },
      {
        caption: "Giving Cherry Cola Her Moment",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-fsobqyan/transcoded/720/cherrycolaevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-fsobqyan/transcoded/720/cherrycolaevergreen.jpg",
      },
      {
        caption: "Sipping on Orange Cream Paradise",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-jcghevir/transcoded/720/orangecreamtropicalrecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-jcghevir/transcoded/720/orangecreamtropicalrecipe.jpg",
      },
      {
        caption: "dirty soda never tasted so good 😍",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-qthmxljs/transcoded/720/docpop-dirtysoda.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-qthmxljs/transcoded/720/docpop-dirtysoda.jpg",
      },
      {
        caption: "ASMR with poppi Grape",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-ibvanorm/transcoded/720/Grape-veryshortvideowithAllison.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-ibvanorm/transcoded/720/Grape-veryshortvideowithAllison.jpg",
      },
      {
        caption: "these berries have gone WILD",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-xwgskvun/transcoded/720/WildBerryRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-xwgskvun/transcoded/720/WildBerryRecipe.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "I absolutely love the variety pack",
        text: "I always buy the wild berry on its own, but I recently been buying the variety pack of the wild berry, lemon lime, and Doc Pop!",
        author: "JaaiiBby18",
      },
      {
        title: "Best new flavor",
        text: "I am a fan of all poppi flavors, I've tried nearly all. Can't beat the classic cherry cola. Orange cream is also really good. Both of these flavors taste just like I would expect them to. Thank you poppi",
        author: "Ninasnork",
      },
      {
        title: "Great Grape flavor",
        text: "Grape and Wildberry are my favorite Poppi flavors. Sweet without being overly sweet.",
        author: "LChop",
      },
      {
        title: "Great taste, less calories and crap!",
        text: "Started drinking Poppi over a year ago but just recently tried the orange cream, and it’s delicious! Reminds me of the ice cream/orange sherbet 😋 Doc Pop, Orange Cream, and Lemon Lime are my favs!",
        author: "MiniQ",
      },
    ],
  },
  "soda-staples": {
    handle: "soda-staples",
    name: "Soda Staples",
    description:
      "Soda lovers, this one’s for you! With some of our most iconic flavors in one pack, your cravings simply don’t stand a chance.",
    packSize: "12 Pack",
    primary: "#fd303a",
    secondary: "#700058",
    gallery: [
      `${A}/live/soda-staples-01.webp`,
      `${A}/live/soda-staples-02.webp`,
      `${A}/live/soda-staples-03.webp`,
      `${A}/live/soda-staples-04.webp`,
      `${A}/live/soda-staples-05.webp`,
      `${A}/live/soda-staples-06.webp`,
    ],
    flavors: [
      { name: "Classic Cola", icon: `${A}/flavor-icons/classic-cola.png`, color: "#e10600", qty: 3 },
      { name: "Cherry Cola", icon: `${A}/flavor-icons/cherry-cola.png`, color: "#700058", qty: 3 },
      { name: "Doc Pop", icon: `${A}/flavor-icons/doc-pop.png`, color: "#f66ff8", qty: 3 },
      { name: "Alpine Blast", icon: `${A}/flavor-icons/alpine-blast.png`, color: "#45ce0d", qty: 3 },
    ],
    nutrition: ["Classic Cola", "Cherry Cola", "Doc Pop", "Alpine Blast"].map(nutritionFor),
    reviews: REVIEWS,
  },
  "trend-setters": {
    handle: "trend-setters",
    upc: "810063711269",
    name: "Trend Setters",
    description:
      "The It flavors of the moment? Yeah, we got ‘em. Grab this pack to get the tastes you’ve seen trending and never miss a beat. ;)",
    packSize: "12 Pack",
    primary: colors.red,
    secondary: colors.pink,
    gallery: [
      `${A}/live/trend-setters-01.webp`,
      `${A}/live/trend-setters-02.webp`,
      `${A}/live/trend-setters-03.webp`,
      `${A}/live/trend-setters-04.webp`,
      `${A}/live/trend-setters-05.webp`,
      `${A}/live/trend-setters-06.webp`,
      `${G}/trend-setters-07.jpg`,
      `${G}/trend-setters-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/trend-setters-01.webp`,
      `${A}/live/trend-setters-02.webp`,
      [`${A}/live/trend-setters-03.webp`, `${A}/live/trend-setters-04.webp`],
      `${A}/live/trend-setters-05.webp`,
      `${A}/live/trend-setters-06.webp`,
      `${G}/trend-setters-07.jpg`,
      `${G}/trend-setters-08.jpg`,
    ],
    flavors: [
      { name: "Orange Cream", icon: `${A}/flavor-icons/orange-cream.png`, color: "#ffe6d1", qty: 3 },
      { name: "Lemon Lime", icon: `${A}/flavor-icons/lemon-lime.png`, color: "#137c21", qty: 3 },
      { name: "Cherry Limeade", icon: `${A}/flavor-icons/cherry-limeade.png`, color: "#d10119", qty: 3 },
      { name: "Raspberry Rose", icon: `${A}/flavor-icons/raspberry-rose.png`, color: colors.pink, qty: 3 },
    ],
    nutrition: ["Raspberry Rose", "Cherry Limeade", "Lemon Lime", "Orange Cream"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5bJxVZ
    tasteVideos: [
      {
        caption: "If Cherry Limeade Could Talk…",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-kirqntjp/transcoded/720/cherrylimeadetiktok.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-kirqntjp/transcoded/720/cherrylimeadetiktok.jpg",
      },
      {
        caption: "Matcha Lemon Lime Refresh",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-juaklxzv/transcoded/720/lemonlime-matcharecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-juaklxzv/transcoded/720/lemonlime-matcharecipe.jpg",
      },
      {
        caption: "berry-utiful <3",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fcjztnhb/transcoded/720/raspberryrose-evergreenrecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fcjztnhb/transcoded/720/raspberryrose-evergreenrecipe.jpg",
      },
      {
        caption: "Orange Cream Dream",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-separwdf/transcoded/720/OrangeCreampour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-separwdf/transcoded/720/OrangeCreampour.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Love this soda!!",
        text: "I had quit drinking soda because of all the sugar... but now that I found these, I can have soda again! It is nice to be able to drink a soda with pizza, or just to have something different, and their flavors are great. I especially like lemon lime and raspberry rose... nice to have alternative to sugary drinks, that I can feel good about my kids drinking once in a while!",
        author: "THawkins",
      },
      {
        title: "Very Positive-I love it",
        text: "I have tried every flavor in the Poppi collection and Cherry Limeade is my favorite flavor",
        author: "Vicki",
      },
      {
        title: "orange cream is the HEAVEN",
        text: "Bought this flavor for the first time, after thinking 3 other flavors were my FAVORITE I am HOOKED...",
        author: "Rev.",
      },
    ],
  },
  "short-list": {
    handle: "short-list",
    upc: "0686603351680",
    name: "Short List",
    description:
      "We’ll keep this short: These are all the flavors you need to get your fruity fix. They’re our top-selling fruit flavors for a reason!",
    packSize: "12 Pack",
    primary: colors.lime,
    secondary: "#d10119",
    gallery: [
      `${A}/live/short-list-01.webp`,
      `${A}/live/short-list-02.webp`,
      `${A}/live/short-list-03.webp`,
      `${A}/live/short-list-04.webp`,
      `${A}/live/short-list-05.webp`,
      `${A}/live/short-list-06.webp`,
      `${G}/short-list-07.jpg`,
      `${G}/short-list-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/short-list-01.webp`,
      `${A}/live/short-list-02.webp`,
      [`${A}/live/short-list-03.webp`, `${A}/live/short-list-04.webp`],
      `${A}/live/short-list-05.webp`,
      `${A}/live/short-list-06.webp`,
      `${G}/short-list-07.jpg`,
      `${G}/short-list-08.jpg`,
    ],
    flavors: [
      { name: "Raspberry Rose", icon: `${A}/flavor-icons/raspberry-rose.png`, color: colors.pink, qty: 3 },
      { name: "Orange", icon: `${A}/flavor-icons/orange.png`, color: colors.orange, qty: 3 },
      { name: "Strawberry Lemon", icon: `${A}/flavor-icons/strawberry-lemon.png`, color: colors.magenta, qty: 3 },
      { name: "Cherry Limeade", icon: `${A}/flavor-icons/cherry-limeade.png`, color: "#d10119", qty: 3 },
    ],
    nutrition: ["Raspberry Rose", "Orange", "Strawberry Lemon", "Cherry Limeade"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist gkYb25
    tasteVideos: [
      {
        caption: "OG Flavor, Forever Iconic",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-lxbauytc/transcoded/720/AllisontalkingaboutSL.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-lxbauytc/transcoded/720/AllisontalkingaboutSL.jpg",
      },
      {
        caption: "Cherry Limeade fancam",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-ezkfsovl/transcoded/720/cherrylimeadepour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-ezkfsovl/transcoded/720/cherrylimeadepour.jpg",
      },
      {
        caption: "Berry in Love with Raspberry Rose",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-flgwxcvo/transcoded/720/rasroseromanticvibe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-flgwxcvo/transcoded/720/rasroseromanticvibe.jpg",
      },
      {
        caption: "Frozen Aperol Spritz with Orange Poppi!",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-ilutqjbk/transcoded/720/orangefrozenaperol.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-ilutqjbk/transcoded/720/orangefrozenaperol.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "My top favorite flavor!",
        text: "This Orange Poppi was the 3rd flavor that I tried of the bunch and has stayed on the top of the list. Followed by Strawberry Lemon and Cherry Limeade. It’s really hard to not like any of them! Will continue to buy them and keep them stocked in my fridge for myself and guests. Great for my work lunches as well!",
        author: "Lissette",
      },
      {
        title: "Full of flavor",
        text: "I’ve been a fan of poppi for years now. And always go for my afternoon pick me up drink during work without grabbing an additional coffee of the day. The cherry limeade, raspberry rose, and orange are definitely my favorite flavors.",
        author: "Obviouslye",
      },
      {
        title: "Delicious!",
        text: "I started drinking Poppi about a month ago. I have tried many of the other brand flavored seltzer waters and I just couldn’t get past the artificial sweetener taste in them. I’m not a big fan of diet sodas either. But Poppi is so good! You still get the soda fizz and the flavor is great. So far, my favorites are strawberry lemon, cherry lime and orange.",
        author: "Amy216",
      },
    ],
  },
  classics: {
    handle: "classics",
    upc: "167380667",
    name: "Classics",
    description:
      "When you’re craving that classic soda taste, look no further than this variety pack. All the iconic flavor, no bad vibes.",
    packSize: "12 Pack",
    primary: "#a40e56",
    secondary: "#f66ff8",
    gallery: [
      `${A}/live/classics-01.webp`,
      `${A}/live/classics-02.webp`,
      `${A}/live/classics-03.webp`,
      `${A}/live/classics-04.webp`,
      `${A}/live/classics-05.webp`,
      `${A}/live/classics-06.webp`,
      `${G}/classics-07.png`,
    ],
    galleryStrip: [
      `${A}/live/classics-01.webp`,
      `${A}/live/classics-02.webp`,
      [`${A}/live/classics-03.webp`, `${A}/live/classics-04.webp`],
      `${A}/live/classics-05.webp`,
      `${A}/live/classics-06.webp`,
      `${G}/classics-07.png`,
    ],
    flavors: [
      { name: "Classic Cola", icon: `${A}/flavor-icons/classic-cola.png`, color: "#e10600", qty: 3 },
      { name: "Doc Pop", icon: `${A}/flavor-icons/doc-pop.png`, color: "#f66ff8", qty: 3 },
      { name: "Root Beer", icon: `${A}/flavor-icons/root-beer.png`, color: "#7c2e3d", qty: 3 },
      { name: "Lemon Lime", icon: `${A}/flavor-icons/lemon-lime.png`, color: "#137c21", qty: 3 },
    ],
    nutrition: ["Doc Pop", "Lemon Lime", "Classic Cola", "Root Beer"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist o9EVpo
    tasteVideos: [
      {
        caption: "ok but have you tried it with lime? 🍋‍🟩",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-gbiqzpvy/transcoded/720/ClassicColaRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-gbiqzpvy/transcoded/720/ClassicColaRecipe.jpg",
      },
      {
        caption: "What’s poppi?",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-zfcajnvm/transcoded/720/docpopinformative.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-zfcajnvm/transcoded/720/docpopinformative.jpg",
      },
      {
        caption: "upgrade ur float 🍨",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-tmdnkujw/transcoded/720/rootbeerfloat.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-tmdnkujw/transcoded/720/rootbeerfloat.jpg",
      },
      {
        caption: "Your New Favorite Lemon Lime Drink",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-vqulfdhb/transcoded/720/LemonLimeRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-vqulfdhb/transcoded/720/LemonLimeRecipe.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Love it",
        text: "We buy the variety pack. I love lemon lime. Even our grandkids enjoy Poppi.",
        author: "Katb007",
      },
      {
        title: "Favorite thing ever",
        text: "I’m obsessed with cola, root beer, and doc pop. Whenever I see them, I have to get one because they are so amazing. So refreshing without all the calories!",
        author: "alli",
      },
      {
        title: "Former Diet Cola Addict!",
        text: "As a lifelong diet soda addict, I can tell you that Poppi has changed my life!! I no longer crave the chemical-ridden sodas that I knew were so bad for me. EVERY POPPI FLAVOR IS AMAZING!! My favorites are Doc Pop, Root Beer and Classic Cola. Refreshing, smooth, no aftertaste and I feel so much better since completely switching to Poppi and no sodas. I love supporting this brand and promoting a healthier lifestyle! THANKS POPPI!!",
        author: "Justine Slayithonestly",
      },
    ],
  },
  "bombshells-variety-pack": {
    handle: "bombshells-variety-pack",
    name: "Bombshells",
    description:
      "This summer’s all about three things: bathing suits, bombshells, and the flavors of love! Drink in every moment with this variety pack made for the hottest season.",
    packSize: "12 Pack",
    primary: "#fed455",
    secondary: "#fe1a88",
    buyUrl: "https://www.amazon.com/dp/B0GMYQP75V",
    gallery: [
      `${A}/live/bombshells-variety-pack-01.webp`,
      `${A}/live/bombshells-variety-pack-02.webp`,
      `${A}/live/bombshells-variety-pack-03.webp`,
      `${A}/live/bombshells-variety-pack-04.webp`,
      `${A}/live/bombshells-variety-pack-05.webp`,
      `${A}/live/bombshells-variety-pack-06.webp`,
    ],
    // live's splide: [hero], [portrait], [two squares stacked], [portrait],
    // [closeup], [square], [tall hand shot]
    galleryStrip: [
      `${A}/bombshells/gallery-1.jpg`,
      `${A}/bombshells/gallery-2.jpg`,
      [`${A}/bombshells/gallery-3.jpg`, `${A}/bombshells/gallery-4.jpg`],
      `${A}/bombshells/gallery-5.jpg`,
      `${A}/bombshells/gallery-6.jpg`,
      `${A}/bombshells/gallery-7.jpg`,
      `${A}/bombshells/gallery-8.jpg`,
    ],
    tasteTiles: [
      `${A}/bombshells/ugc-1.png`,
      `${A}/bombshells/ugc-2.png`,
      `${A}/bombshells/ugc-3.png`,
      `${A}/bombshells/ugc-4.png`,
      `${A}/bombshells/ugc-5.png`,
    ],
    flavors: [
      { name: "Lemon Lime", icon: `${A}/bombshells/includes-lemon-lime.png`, color: "#137c21", qty: 2 },
      { name: "Orange Cream", icon: `${A}/bombshells/includes-orange-cream.png`, color: "#ffe6d1", qty: 2 },
      { name: "Punch Pop", icon: `${A}/bombshells/includes-punch-pop.jpg`, color: "#ffffff", qty: 2 },
      { name: "Strawberry Lemon", icon: `${A}/bombshells/includes-strawberry-lemon.png`, color: colors.magenta, qty: 2 },
      { name: "Wild Berry", icon: `${A}/bombshells/includes-wild-berry.png`, color: colors.cyan, qty: 2 },
      { name: "Lemonade", icon: `${A}/bombshells/includes-lemonade.jpg`, color: "#fed455", qty: 2 },
    ],
    // live nutrition carousel, one slide per flavor, values verbatim
    nutrition: [
      {
        flavor: "Lemon Lime", calories: 35, totalFat: "0g", sodium: "0mg",
        carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%", sugars: "5g",
        addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
        ingredients: "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Apple Juice*, Organic Apple Cider Vinegar, Natural Flavors, Citric Acid, Organic Agave Inulin, Stevia Leaf Extract, Lime Juice*, Lemon Juice*.",
        notes: ["*Concentrate", "Contains 4% Juice"],
      },
      {
        flavor: "Orange Cream", calories: 30, totalFat: "0g", sodium: "0mg",
        carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%", sugars: "5g",
        addedSugars: "3g", addedSugarsPct: "6%", protein: "0g",
        ingredients: "Sparkling Water, Cassava Root Fiber, Apple Juice*, Organic Cane Sugar, Organic Apple Cider Vinegar, Natural Flavors, Citric Acid, Organic Agave Inulin, Stevia Leaf Extract, Orange Juice*, Beta Carotene (color).",
        notes: ["*Concentrate", "Contains 5% Juice"],
      },
      {
        flavor: "Punch Pop", calories: 30, totalFat: "0g", sodium: "0mg",
        carbs: "9g", carbsPct: "3%", fiber: "3", fiberPct: "11%", sugars: "5g",
        addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
        ingredients: "Sparkling Water, Organic Cane Sugar, Cassava Root Fiber, Apple Juice Concentrate, Citric Acid, Natural Flavors, Organic Apple Cider Vinegar, Vegetable Juice Color, Organic Agave Inulin, Stevia Leaf Extract.",
        notes: ["Contains 3% Juice"],
      },
      {
        flavor: "Strawberry Lemon", calories: 30, totalFat: "0g", sodium: "0mg",
        carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%", sugars: "5g",
        addedSugars: "4g", addedSugarsPct: "8%", protein: "0g",
        ingredients: "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Organic Apple Cider Vinegar, Natural Flavors, Lemon Juice*, Strawberry Juice*, Organic Agave Inulin, Stevia Leaf Extract, Vegetable Juice (color).",
        notes: ["*Concentrate", "Contains 6% Juice"],
      },
      {
        flavor: "Wild Berry", calories: 30, totalFat: "0g", sodium: "5mg",
        carbs: "8g", carbsPct: "3%", fiber: "2g", fiberPct: "7%", sugars: "5g",
        addedSugars: "5g", addedSugarsPct: "10%", protein: "0g",
        ingredients: "Sparkling Water, Organic Cane Sugar, Organic Apple Cider Vinegar, Apple Juice*, Organic Agave Inulin, Natural Flavor, Citric Acid, Stevia, Vegetable Juice Color.",
        notes: ["*Concentrate."],
      },
      {
        flavor: "Lemonade", calories: 30, totalFat: "0g", sodium: "0mg",
        carbs: "9g", carbsPct: "3%", fiber: "3g", fiberPct: "11%", sugars: "5g",
        addedSugars: "3g", addedSugarsPct: "6%", protein: "0",
        ingredients: "Sparkling Water, Cassava Root Fiber, Organic Cane Sugar, Apple Juice Concentrate, Natural Flavors, Vegetable Juice (color), Organic Apple Cider Vinegar, Citric Acid, Organic Agave Inulin, Stevia Leaf Extract.",
        notes: ["Contains 3% Juice"],
      },
    ],
    // live shows exactly these two review cards on loop
    reviews: [
      {
        title: "THE BEST FLAVOR EVER",
        text: "I love Poppi Punch Pop! It's fruity, sweet and so refreshing!",
        author: "emma555",
      },
      {
        title: "Best Summer Drink!",
        text: "So delicous! My mom surprised me with this and I was astounded at how good it tasted!",
        author: "EdZDG",
      },
    ],
  },

  // ---- single flavors (collection tokens: primary=from, secondary=to) ----
  "sour-apple": {
    handle: "sour-apple",
    upc: "10810063712805",
    name: "Sour Apple",
    description:
      "Don’t let it fool you… This limited-edition Sour Apple soda might be sweet, but it defff has a bite. Deliciously tart with a sour kick, it’s the twist your taste buds didn’t see coming. And with 5g sugar, ingredients you can love and prebiotics, it’s good to the core.",
    packSize: "12 Pack",
    // live re-themed sour-apple to #bcff00 (verified 2026-08-25)
    primary: "#bcff00",
    secondary: "#3e8549",
    gallery: [
      `${SA}/pdp-1.jpg`,
      `${SA}/pdp-2.jpg`,
      `${SA}/pdp-3.jpg`,
      `${SA}/pdp-4.jpg`,
      `${SA}/pdp-5.jpg`,
      `${SA}/pdp-6.jpg`,
      `${SA}/pdp-7.jpg`,
    ],
    galleryStrip: [
      `${SA}/pdp-1.jpg`,
      `${SA}/pdp-2.jpg`,
      [`${SA}/pdp-3.jpg`, `${SA}/pdp-4.jpg`],
      `${SA}/pdp-5.jpg`,
      `${SA}/pdp-6.jpg`,
      `${SA}/pdp-7.jpg`,
    ],
    flavors: [
      { name: "Sour Apple", icon: `${SA}/icon.jpg`, color: "#bcff00", qty: 12 },
    ],
    nutrition: ["Sour Apple"].map(nutritionFor),
    hideTaste: true,
    // live sour-apple has no customer-reviews section
    reviews: [],
  },
  "punch-pop": {
    handle: "punch-pop",
    name: "Punch Pop",
    description:
      "Is it hot out here… Or is it just you? Quench thirsty behavior and avoid the ick with a refreshing fruit punch that’s more exciting than the villa’s new bombshell.",
    packSize: "12 Pack",
    primary: "#ff00d0",
    secondary: "#ffffff",
    gallery: [
      `${A}/live/punch-pop-01.webp`,
      `${A}/live/punch-pop-02.webp`,
      `${A}/live/punch-pop-03.webp`,
      `${A}/live/punch-pop-04.webp`,
      `${A}/live/punch-pop-05.webp`,
      `${A}/live/punch-pop-06.webp`,
    ],
    flavors: [{ name: "Punch Pop", icon: `${A}/flavor-icons/punch-pop.jpg`, color: "#ffffff", qty: 12 }],
    nutrition: ["Punch Pop"].map(nutritionFor),
    reviews: REVIEWS,
  },
  "shirley-temple": {
    handle: "shirley-temple",
    upc: "810063712471",
    name: "Shirley Temple",
    description:
      "You’ve always had good taste, which is why your fave childhood drink was a Shirley Temple. Now you’ve had a glow up… and so has this iconic flavor.",
    packSize: "12 Pack",
    primary: "#f26b6b",
    secondary: "#8e0011",
    gallery: [
      `${A}/live/shirley-temple-01.webp`,
      `${A}/live/shirley-temple-02.webp`,
      `${A}/live/shirley-temple-03.webp`,
      `${A}/live/shirley-temple-04.webp`,
      `${A}/live/shirley-temple-05.webp`,
      `${A}/live/shirley-temple-06.webp`,
      `${G}/shirley-temple-07.jpg`,
      `${G}/shirley-temple-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/shirley-temple-01.webp`,
      `${A}/live/shirley-temple-02.webp`,
      [`${A}/live/shirley-temple-03.webp`, `${A}/live/shirley-temple-04.webp`],
      `${A}/live/shirley-temple-05.webp`,
      `${A}/live/shirley-temple-06.webp`,
      `${G}/shirley-temple-07.jpg`,
      `${G}/shirley-temple-08.jpg`,
    ],
    flavors: [{ name: "Shirley Temple", icon: `${A}/flavor-icons/shirley-temple.png`, color: "#8e0011", qty: 12 }],
    nutrition: ["Shirley Temple"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5R7OKN
    tasteVideos: [
      {
        caption: "a new star just arrived on the scene 🍒✨🎥",
        src: "https://cdn4.fireworktv.com/medias/2026/1/6/1767737492-toywuzcs/transcoded/720/SHIRLEY20LAUNCH20UGC.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2026/1/6/1767737492-toywuzcs/transcoded/720/SHIRLEY20LAUNCH20UGC.jpg",
      },
      {
        caption: "fridge restock for miss temple🍒✨",
        src: "https://cdn4.fireworktv.com/medias/2026/1/6/1767737492-gkqoufyb/transcoded/720/SHIRLEY20FRIDGE20RESTOCK.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2026/1/6/1767737492-gkqoufyb/transcoded/720/SHIRLEY20FRIDGE20RESTOCK.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Sweet nostalgia!!!",
        text: "I bought these a week ago and to say I’m obsessed is an understatement! Sweet cherry and a crisp citrus flavors bring you back to your childhood, when you thought you were cool sipping a “cocktail” like the grownups",
        author: "That Girl",
      },
      {
        title: "Flavorfully Amazing",
        text: "This Shirley Temple poppi is like a hug from my childhood!",
        author: "Maggiemay",
      },
      {
        title: "my new go-to flavor!!",
        text: "Absolutely hooked on this flavor! My new favorite! All my friends and family are TOTALLY obsessed!",
        author: "cbarry",
      },
    ],
  },
  "orange-cream": {
    handle: "orange-cream",
    upc: "810063710880",
    name: "Orange Cream",
    description:
      "You hear that? It’s the sound of your tastebuds' dreams coming true. Smooth vanilla joins zesty orange for a flavor that’s simultaneously nostalgic and totally refreshing.",
    packSize: "12 Pack",
    primary: "#ffe6d1",
    secondary: "#e15505",
    gallery: [
      `${A}/live/orange-cream-01.webp`,
      `${A}/live/orange-cream-02.webp`,
      `${A}/live/orange-cream-03.webp`,
      `${A}/live/orange-cream-04.webp`,
      `${A}/live/orange-cream-05.webp`,
      `${A}/live/orange-cream-06.webp`,
      `${G}/orange-cream-07.jpg`,
      `${G}/orange-cream-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/orange-cream-01.webp`,
      `${A}/live/orange-cream-02.webp`,
      [`${A}/live/orange-cream-03.webp`, `${A}/live/orange-cream-04.webp`],
      `${A}/live/orange-cream-05.webp`,
      `${A}/live/orange-cream-06.webp`,
      `${G}/orange-cream-07.jpg`,
      `${G}/orange-cream-08.jpg`,
    ],
    flavors: [{ name: "Orange Cream", icon: `${A}/flavor-icons/orange-cream.png`, color: "#e15505", qty: 12 }],
    nutrition: ["Orange Cream"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5a4Edv
    tasteVideos: [
      {
        caption: "Orange Cream hive rise!!",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-fkcqojxh/transcoded/720/twinningwithorangecream.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-fkcqojxh/transcoded/720/twinningwithorangecream.jpg",
      },
      {
        caption: "Sipping on Orange Cream Paradise",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-jcghevir/transcoded/720/orangecreamtropicalrecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-jcghevir/transcoded/720/orangecreamtropicalrecipe.jpg",
      },
      {
        caption: "the perfect spritz ✨",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-wqtlcfnm/transcoded/720/OrangeCreamSpritz.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-wqtlcfnm/transcoded/720/OrangeCreamSpritz.jpg",
      },
      {
        caption: "Orange Cream Dream",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-separwdf/transcoded/720/OrangeCreampour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239487-separwdf/transcoded/720/OrangeCreampour.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Poppi is a gift from the heavens!",
        text: "The orange cream Poppi is so spot on! It’s hands down my new favorite flavor and I can’t get enough! Try it if you haven’t already!",
        author: "Ambi",
      },
      {
        title: "Orange Dream!",
        text: "If you liked the orange creamsicle ice cream pops as a child this flavor will bring you back to a summer day. It’s like Cream Soda with the brightness of orange. It’s incredibly refreshing! One of my favorites!",
        author: "JenM",
      },
      {
        title: "Orange cream is the HEAVEN",
        text: "Bought this flavor for the first time after thinking 3 other flavors were my FAVORITE. I am HOOKED...",
        author: "Rev.",
      },
      {
        title: "Best Flavor Ever!",
        text: "I have the orange cream on repeat. It tastes just like it did when I was a kid. Smooth, creamy, and delicious.",
        author: "Heathaa",
      },
    ],
  },
  "strawberry-lemon": {
    handle: "strawberry-lemon",
    upc: "709586514856",
    name: "Strawberry Lemon",
    description:
      "Wanna get juicy? Sip on this super refreshing n’ tasty blend of ripe strawberry and crisp lemon that’s so bold, it just asked for your number.",
    packSize: "12 Pack",
    primary: colors.magenta,
    secondary: colors.yellow,
    gallery: [
      `${A}/live/strawberry-lemon-01.webp`,
      `${A}/live/strawberry-lemon-02.webp`,
      `${A}/live/strawberry-lemon-03.webp`,
      `${A}/live/strawberry-lemon-04.webp`,
      `${A}/live/strawberry-lemon-05.webp`,
      `${A}/live/strawberry-lemon-06.webp`,
      `${G}/strawberry-lemon-07.jpg`,
      `${G}/strawberry-lemon-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/strawberry-lemon-01.webp`,
      `${A}/live/strawberry-lemon-02.webp`,
      [`${A}/live/strawberry-lemon-03.webp`, `${A}/live/strawberry-lemon-04.webp`],
      `${A}/live/strawberry-lemon-05.webp`,
      `${A}/live/strawberry-lemon-06.webp`,
      `${G}/strawberry-lemon-07.jpg`,
      `${G}/strawberry-lemon-08.jpg`,
    ],
    flavors: [{ name: "Strawberry Lemon", icon: `${A}/flavor-icons/strawberry-lemon.png`, color: colors.yellow, qty: 12 }],
    nutrition: ["Strawberry Lemon"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5xbMGg
    tasteVideos: [
      {
        caption: "your everyday pick me up 🍓🍋",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-sqjtudhe/transcoded/720/strawberrylemonwfruit.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-sqjtudhe/transcoded/720/strawberrylemonwfruit.jpg",
      },
      {
        caption: "Strawberry + Lemon = BEST Combo 🍓🍋",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-thpguxls/transcoded/720/strawberrylemontapspour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-thpguxls/transcoded/720/strawberrylemontapspour.jpg",
      },
      {
        caption: "most refreshing thing you'll ever sip",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-reponxhg/transcoded/720/StrawberryLemonRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-reponxhg/transcoded/720/StrawberryLemonRecipe.jpg",
      },
      {
        caption: "Grocery Store Haul",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-iavgxkzr/transcoded/720/SLinstoreTT.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-iavgxkzr/transcoded/720/SLinstoreTT.jpg",
      },
      {
        caption: "OG Flavor, Forever Iconic",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-lxbauytc/transcoded/720/AllisontalkingaboutSL.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239419-lxbauytc/transcoded/720/AllisontalkingaboutSL.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "So delicious!!!!",
        text: "All the flavors are so good!!! But the strawberry lemon is absolutely perfect.",
        author: "Rells",
      },
      {
        title: "I don’t like soda‚ but I LOVE this.",
        text: "I discovered these bc my work brings them in, and Strawberry Lemon is the perfect sweet and zesty flavor. I LOVE IT. I love that they’re low sugar too.",
        author: "Kat10",
      },
      {
        title: "Best flavor",
        text: "Poppi drinks are the very best. My favorite is the strawberry lemon. It is such a refreshing drink. I can share them with my grandson without worry of too much sugar. Poppi is always on my shopping list.",
        author: "Leonna61",
      },
    ],
  },
  "cream-soda": {
    handle: "cream-soda",
    upc: "810063711481",
    name: "Cream Soda",
    description:
      "Need us to pinch you? ‘Cause you’re definitely not dreaming… This Cream Soda has the velvety, lush vanilla taste you crave, but without the usual soda shop sugar.",
    packSize: "12 Pack",
    primary: "#0667be",
    secondary: "#e9e4c5",
    gallery: [
      `${A}/live/cream-soda-01.webp`,
      `${A}/live/cream-soda-02.webp`,
      `${G}/cream-soda-03.jpg`,
      `${A}/live/cream-soda-03.webp`,
      `${A}/live/cream-soda-04.webp`,
      `${A}/live/cream-soda-05.webp`,
      `${G}/cream-soda-07.jpg`,
    ],
    galleryStrip: [
      `${A}/live/cream-soda-01.webp`,
      `${A}/live/cream-soda-02.webp`,
      [`${G}/cream-soda-03.jpg`, `${A}/live/cream-soda-03.webp`],
      `${A}/live/cream-soda-04.webp`,
      `${A}/live/cream-soda-05.webp`,
      `${G}/cream-soda-07.jpg`,
    ],
    flavors: [{ name: "Cream Soda", icon: `${A}/flavor-icons/cream-soda.jpg`, color: "#e9e4c5", qty: 12 }],
    nutrition: ["Cream Soda"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist vJ03dN
    tasteVideos: [
      {
        caption: "made for each other 💕",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-uybgmiox/transcoded/720/creamsoda-float.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-uybgmiox/transcoded/720/creamsoda-float.jpg",
      },
      {
        caption: "what dreams are made of 🍦",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-xqdcmpbw/transcoded/720/creamsodapour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-xqdcmpbw/transcoded/720/creamsodapour.jpg",
      },
      {
        caption: "Smooth, Sweet, & Ninja-CREAMi Approved",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-fgudiotb/transcoded/720/creamsoda-ninjacreami.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240339-fgudiotb/transcoded/720/creamsoda-ninjacreami.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "10/10 DELICIOUS",
        text: "The Cream Soda is my favorite poppi EVER! So delicious and true to the creamy vanilla flavor every good cream soda has!!! LOVE!",
        author: "Brielle",
      },
      {
        title: "OBSESSED",
        text: "I have always been wanting to try a prebiotic soda since I am a big soda girlie, and I am so glad I did. I got my hands on the Cream Soda flavor and needless to say I’m obsessed. Definitely purchasing again and can’t wait to try out some more flavors!",
        author: "kellyg0709",
      },
      {
        title: "When you DREAM about Poppi CREAM Soda",
        text: "I purchased poppi Cream Soda as soon as it was released, and I immediately became obsessed with it. I loved it so much that I ended up ordering a whole case. I already loved poppi and had some favorite flavors, but now Cream Soda has definitely been added to the list.",
        author: "Nene23",
      },
    ],
  },
  "ginger-lime": {
    handle: "ginger-lime",
    upc: "709586514870",
    name: "Ginger Lime",
    description:
      "What’s someone like you doing in a place like this? If you’re lookin’ for a soda that’ll treat you real good, well, allow us to introduce ourselves. ;)",
    packSize: "12 Pack",
    primary: colors.lime,
    secondary: colors.blue,
    gallery: [
      `${A}/live/ginger-lime-01.webp`,
      `${A}/live/ginger-lime-02.webp`,
      `${A}/live/ginger-lime-03.webp`,
      `${A}/live/ginger-lime-04.webp`,
      `${A}/live/ginger-lime-05.webp`,
      `${A}/live/ginger-lime-06.webp`,
      `${G}/ginger-lime-07.jpg`,
      `${G}/ginger-lime-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/ginger-lime-01.webp`,
      `${A}/live/ginger-lime-02.webp`,
      [`${A}/live/ginger-lime-03.webp`, `${A}/live/ginger-lime-04.webp`],
      `${A}/live/ginger-lime-05.webp`,
      `${A}/live/ginger-lime-06.webp`,
      `${G}/ginger-lime-07.jpg`,
      `${G}/ginger-lime-08.jpg`,
    ],
    flavors: [{ name: "Ginger Lime", icon: `${A}/flavor-icons/ginger-lemon.png`, color: colors.blue, qty: 12 }],
    nutrition: ["Ginger Lime"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist o0lbRo
    tasteVideos: [
      {
        caption: "Ginger Lime Flavor Flow",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-fehovazn/transcoded/720/toreplacewithoutwatermarkoncelive.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-fehovazn/transcoded/720/toreplacewithoutwatermarkoncelive.jpg",
      },
      {
        caption: "It’s Ginger Lime Time",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-zukwvrxt/transcoded/720/gingerlimepour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-zukwvrxt/transcoded/720/gingerlimepour.jpg",
      },
      {
        caption: "we 💚 Ginger Lime",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-ibxgtyes/transcoded/720/gingerlimeevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239284-ibxgtyes/transcoded/720/gingerlimeevergreen.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "INCREDIBLE",
        text: "My actual favorite beverage that I could drink every single day. The perfect amount of ginger with lime.",
        author: "Quinn3",
      },
      {
        title: "LOVE THIS STUFF!",
        text: "I love all the Poppi flavors, but the classic ginger lime combo is perfection when done by Poppi. Thank you to the creators!",
        author: "ETZG",
      },
      {
        title: "Hardly drink soda but love this",
        text: "In our household, we absolutely love Ginger Lime Poppi!",
        author: "KTM1223",
      },
    ],
  },
  "alpine-blast": {
    handle: "alpine-blast",
    upc: "810063711702",
    caffeineBadge: true,
    name: "Alpine Blast",
    description:
      "They say lightning doesn’t strike twice, but maybe that’s just ’cause they haven’t tasted this energizing burst of citrus flavor yet.",
    packSize: "12 Pack",
    primary: "#45ce0d",
    secondary: "#0031a7",
    gallery: [
      `${A}/live/alpine-blast-01.webp`,
      `${A}/live/alpine-blast-02.webp`,
      `${A}/live/alpine-blast-03.webp`,
      `${A}/live/alpine-blast-04.webp`,
      `${A}/live/alpine-blast-05.webp`,
      `${A}/live/alpine-blast-06.webp`,
      `${G}/alpine-blast-07.jpg`,
      `${G}/alpine-blast-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/alpine-blast-01.webp`,
      `${A}/live/alpine-blast-02.webp`,
      [`${A}/live/alpine-blast-03.webp`, `${A}/live/alpine-blast-04.webp`],
      `${A}/live/alpine-blast-05.webp`,
      `${A}/live/alpine-blast-06.webp`,
      `${G}/alpine-blast-07.jpg`,
      `${G}/alpine-blast-08.jpg`,
    ],
    flavors: [{ name: "Alpine Blast", icon: `${A}/flavor-icons/alpine-blast.png`, color: "#0031a7", qty: 12 }],
    nutrition: ["Alpine Blast"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5EWQP2
    tasteVideos: [
      {
        caption: "A Peak Pop Moment",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-cxtuflpi/transcoded/720/alpineblast-teaser.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-cxtuflpi/transcoded/720/alpineblast-teaser.jpg",
      },
      {
        caption: "Full Blast Flavor Mode",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-xaeugicy/transcoded/720/alpineblastpourteaser.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-xaeugicy/transcoded/720/alpineblastpourteaser.jpg",
      },
      {
        caption: "Alpine Blast Fridge Restock!",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-xagwdcnk/transcoded/720/alpineblastfridgerestock.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-xagwdcnk/transcoded/720/alpineblastfridgerestock.jpg",
      },
      {
        caption: "Meet Alpine Blast",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-fqkxvtop/transcoded/720/AllisontalkingaboutAB.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240289-fqkxvtop/transcoded/720/AllisontalkingaboutAB.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Love The Taste!",
        text: "I tried this for the first time, and I am absolutely in love with it! The flavors are blended perfectly with the carbonation, and the overall experience of tasting it is unforgettable!",
        author: "KG853",
      },
      {
        title: "My absolute favorite poppi flavor",
        text: "I was a big doc pop fan, but THIS Alpine Blast one takes the cake!!! Can’t get enough of it, it’s refreshing and caffeinated. Love it!!",
        author: "courtonthefly",
      },
      {
        title: "Citrusy Goodness",
        text: "I buy two poppi sodas a week, and Alpine Blast is one of my favorites! It is citrusy and bubbly and makes my week very enjoyable. I feel ecstatic for having stumbled upon this product.",
        author: "LC2012lc",
      },
    ],
  },
  "doc-pop": {
    handle: "doc-pop",
    upc: "810063710309",
    caffeineBadge: true,
    name: "Doc Pop",
    description:
      "Baby, you’re a classic. And so is the soda in your hand. It’s got that iconic flavor the doctor's famous for, but without all the extra sugar.",
    packSize: "12 Pack",
    primary: "#f66ff8",
    secondary: "#a40e56",
    gallery: [
      `${A}/live/doc-pop-01.webp`,
      `${A}/live/doc-pop-02.webp`,
      `${A}/live/doc-pop-03.webp`,
      `${A}/live/doc-pop-04.webp`,
      `${A}/live/doc-pop-05.webp`,
      `${A}/live/doc-pop-06.webp`,
      `${G}/doc-pop-07.jpg`,
    ],
    galleryStrip: [
      `${A}/live/doc-pop-01.webp`,
      `${A}/live/doc-pop-02.webp`,
      [`${A}/live/doc-pop-03.webp`, `${A}/live/doc-pop-04.webp`],
      `${A}/live/doc-pop-05.webp`,
      `${A}/live/doc-pop-06.webp`,
      `${G}/doc-pop-07.jpg`,
    ],
    flavors: [{ name: "Doc Pop", icon: `${A}/flavor-icons/doc-pop.png`, color: "#a40e56", qty: 12 }],
    nutrition: ["Doc Pop"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5zbZe5
    tasteVideos: [
      {
        caption: "What’s poppi?",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-zfcajnvm/transcoded/720/docpopinformative.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-zfcajnvm/transcoded/720/docpopinformative.jpg",
      },
      {
        caption: "nothing like a crispy Doc Pop",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-tjdwnygu/transcoded/720/docpopevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-tjdwnygu/transcoded/720/docpopevergreen.jpg",
      },
      {
        caption: "dirty soda never tasted so good 😍",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-qthmxljs/transcoded/720/docpop-dirtysoda.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-qthmxljs/transcoded/720/docpop-dirtysoda.jpg",
      },
      {
        caption: "Kiss the Fizz",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-afnmghqy/transcoded/720/allisonstephencute.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239872-afnmghqy/transcoded/720/allisonstephencute.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Delicious drink!",
        text: "I never get tired of this drink! The Doc Pop is so yummy and refreshing. Something I always need to have on hand!",
        author: "alexa2324",
      },
      {
        title: "The BEST",
        text: "Doc Pop is the BEST flavor by far! I started Poppi about a year ago and I haven’t gone back to regular pop! Love love love this product.",
        author: "Pekillop",
      },
      {
        title: "Incredible Taste",
        text: "I am obsessed with Doc Pop. I crave it every day and I never used to drink pop.",
        author: "Meghan",
      },
    ],
  },
  "wild-berry-12oz": {
    handle: "wild-berry-12oz",
    upc: "810063711078",
    name: "Wild Berry",
    description:
      "Have you heard? These berries are drama. Raspberry’s into blueberry, blueberry’s into strawberry, and we’re into all three of them together. Oop!",
    packSize: "12 Pack",
    primary: colors.cyan,
    secondary: colors.purple,
    gallery: [
      `${A}/live/wild-berry-12oz-01.webp`,
      `${A}/live/wild-berry-12oz-02.webp`,
      `${A}/live/wild-berry-12oz-03.webp`,
      `${A}/live/wild-berry-12oz-04.webp`,
      `${A}/live/wild-berry-12oz-05.webp`,
      `${A}/live/wild-berry-12oz-06.webp`,
      `${G}/wild-berry-12oz-07.jpg`,
      `${G}/wild-berry-12oz-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/wild-berry-12oz-01.webp`,
      `${A}/live/wild-berry-12oz-02.webp`,
      [`${A}/live/wild-berry-12oz-03.webp`, `${A}/live/wild-berry-12oz-04.webp`],
      `${A}/live/wild-berry-12oz-05.webp`,
      `${A}/live/wild-berry-12oz-06.webp`,
      `${G}/wild-berry-12oz-07.jpg`,
      `${G}/wild-berry-12oz-08.jpg`,
    ],
    flavors: [{ name: "Wild Berry", icon: `${A}/flavor-icons/wild-berry.png`, color: colors.purple, qty: 12 }],
    nutrition: ["Wild Berry"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist o8ympg
    tasteVideos: [
      {
        caption: "these berries have gone WILD",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-xwgskvun/transcoded/720/WildBerryRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-xwgskvun/transcoded/720/WildBerryRecipe.jpg",
      },
      {
        caption: "ASMR with Wild Berry",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-pnxwjsho/transcoded/720/wildberryasmr.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-pnxwjsho/transcoded/720/wildberryasmr.jpg",
      },
      {
        caption: "The Berry Best Soda!",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-rtlscfxn/transcoded/720/wildberry12pk.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239774-rtlscfxn/transcoded/720/wildberry12pk.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Great Taste!",
        text: "Wild Berry has become my ultimate go-to soda from Poppi! The flavor is perfect. It isn't too sweet or too sour. Definitely crave this more than any other soda I used to drink. Highly recommend!",
        author: "amb072893",
      },
      {
        title: "Our Absolute Favorite!",
        text: "The Wild Berry flavor is my husband and I's favorite! It's sweet but not too sweet and is so yummy! The perfect treat that doesn't come with any bad side effects. Love it!",
        author: "mcarter07",
      },
      {
        title: "Amazingly delicious!",
        text: "The berry tastes amazing!!! It has the right amount of flavor to give you a taste of each berry! My favorite one for sure!",
        author: "Melissa",
      },
      {
        title: "Favorite!",
        text: "Wild berry is a favorite of mine and my kids! I have to hide it from them so I can have my own little treat!",
        author: "JessT",
      },
    ],
  },
  "lemon-lime": {
    handle: "lemon-lime",
    upc: "810063710316",
    name: "Lemon Lime",
    description:
      "True love never dies. And the romance between lemon and lime? It’s forever <3.",
    packSize: "12 Pack",
    primary: "#137c21",
    secondary: colors.yellow,
    gallery: [
      `${A}/live/lemon-lime-01.webp`,
      `${A}/live/lemon-lime-02.webp`,
      `${A}/live/lemon-lime-03.webp`,
      `${A}/live/lemon-lime-04.webp`,
      `${A}/live/lemon-lime-05.webp`,
      `${A}/live/lemon-lime-06.webp`,
      `${G}/lemon-lime-07.jpg`,
      `${G}/lemon-lime-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/lemon-lime-01.webp`,
      `${A}/live/lemon-lime-02.webp`,
      [`${A}/live/lemon-lime-03.webp`, `${A}/live/lemon-lime-04.webp`],
      `${A}/live/lemon-lime-05.webp`,
      `${A}/live/lemon-lime-06.webp`,
      `${G}/lemon-lime-07.jpg`,
      `${G}/lemon-lime-08.jpg`,
    ],
    flavors: [{ name: "Lemon Lime", icon: `${A}/flavor-icons/lemon-lime.png`, color: colors.yellow, qty: 12 }],
    nutrition: ["Lemon Lime"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist o8Nwjp
    tasteVideos: [
      {
        caption: "we have a crush... on Lemon Lime 🤭",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ivndcxjs/transcoded/720/lemonlime-whimsical.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ivndcxjs/transcoded/720/lemonlime-whimsical.jpg",
      },
      {
        caption: "Your New Favorite Lemon Lime Drink",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-vqulfdhb/transcoded/720/LemonLimeRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-vqulfdhb/transcoded/720/LemonLimeRecipe.jpg",
      },
      {
        caption: "r u refreshed yet? 😏",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ltbzxrow/transcoded/720/LemonLimePour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-ltbzxrow/transcoded/720/LemonLimePour.jpg",
      },
      {
        caption: "Matcha Lemon Lime Refresh",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-juaklxzv/transcoded/720/lemonlime-matcharecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239958-juaklxzv/transcoded/720/lemonlime-matcharecipe.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Refreshing",
        text: "Crisp and refreshing! The whole family loves it. Lemon Lime is by far our favorite flavor.",
        author: "cya777",
      },
      {
        title: "Fam Fav",
        text: "We love Poppi in our house, but Lemon Lime is my daughter’s absolute favorite!",
        author: "Pan44",
      },
      {
        title: "Better than any other lemon lime soda!",
        text: "This flavor is so bright and refreshing. With little sugar, it is satiating and so bright! It’s not too tart, but I can taste both lemon and lime. It’s really so delicious!",
        author: "JenM",
      },
    ],
  },
  "rootbeer": {
    handle: "rootbeer",
    upc: "810063710323",
    name: "Root Beer",
    description:
      "Here’s the scoop: you’re defff gonna want to pick up some vanilla ice cream to go with this iconic flavor. Float, anyone?",
    packSize: "12 Pack",
    primary: "#7c2e3d",
    secondary: "#f9e26d",
    gallery: [
      `${A}/live/rootbeer-01.webp`,
      `${A}/live/rootbeer-02.webp`,
      `${A}/live/rootbeer-03.webp`,
      `${A}/live/rootbeer-04.webp`,
      `${A}/live/rootbeer-05.webp`,
      `${A}/live/rootbeer-06.webp`,
      `${G}/rootbeer-07.jpg`,
      `${G}/rootbeer-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/rootbeer-01.webp`,
      `${A}/live/rootbeer-02.webp`,
      [`${A}/live/rootbeer-03.webp`, `${A}/live/rootbeer-04.webp`],
      `${A}/live/rootbeer-05.webp`,
      `${A}/live/rootbeer-06.webp`,
      `${G}/rootbeer-07.jpg`,
      `${G}/rootbeer-08.jpg`,
    ],
    flavors: [{ name: "Root Beer", icon: `${A}/flavor-icons/root-beer.png`, color: "#f9e26d", qty: 12 }],
    nutrition: ["Root Beer"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist o0lExo
    tasteVideos: [
      {
        caption: "upgrade ur float 🍨",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-tmdnkujw/transcoded/720/rootbeerfloat.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-tmdnkujw/transcoded/720/rootbeerfloat.jpg",
      },
      {
        caption: "Root Beer + Cream = Dirty Soda Heaven",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-tkxiajbo/transcoded/720/rootbeerevergeenrecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-tkxiajbo/transcoded/720/rootbeerevergeenrecipe.jpg",
      },
      {
        caption: "delicious... and gorgeous",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-syizjhlq/transcoded/720/OOHaesthetic--teaser.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239661-syizjhlq/transcoded/720/OOHaesthetic--teaser.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Hands down the best drink I’ve ever had",
        text: "Poppi root beer changed my life!! Ever since the first time I had it, I’ve been hooked!! The fact that it tastes so sweet yet is only 5g of sugar always amazes me!! Definitely a customer for life!",
        author: "NicholeG",
      },
      {
        title: "Best Root Beer ever!!",
        text: "I love my root beer sodas, for the longest time until I had to cut it off. LUCKY, Poppi came into my life in a healthier version where I can enjoy. This Poppi root beer tastes just the same with better ingredients. 10 for 10 recommended!",
        author: "Nammy",
      },
      {
        title: "Root Beer",
        text: "I love the flavor and love the fact that it is so much better for you!",
        author: "RunnerMoma",
      },
      {
        title: "Delicious and it smells like old-time Root beer",
        text: "I love Root beer but I wanted a healthier alternative so I tried Poppi root beer and it is delicious. Even my husband loves the taste, and he is a lover of sugar. So when he says it's good and he wants that instead of the real thing, I knew I found a winner!!",
        author: "Amying4health1",
      },
    ],
  },
  "orange": {
    handle: "orange",
    upc: "724190812877",
    name: "Orange",
    description:
      "Knock, knock. Orange you glad we didn’t say tons of sugar? Okay, yeah, we didn’t land that joke, but we absolutely nailed classic orange soda.",
    packSize: "12 Pack",
    primary: colors.orange,
    secondary: colors.cream,
    gallery: [
      `${A}/live/orange-01.webp`,
      `${A}/live/orange-02.webp`,
      `${A}/live/orange-03.webp`,
      `${A}/live/orange-04.webp`,
      `${A}/live/orange-05.webp`,
      `${A}/live/orange-06.webp`,
      `${G}/orange-07.jpg`,
      `${G}/orange-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/orange-01.webp`,
      `${A}/live/orange-02.webp`,
      [`${A}/live/orange-03.webp`, `${A}/live/orange-04.webp`],
      `${A}/live/orange-05.webp`,
      `${A}/live/orange-06.webp`,
      `${G}/orange-07.jpg`,
      `${G}/orange-08.jpg`,
    ],
    flavors: [{ name: "Orange", icon: `${A}/flavor-icons/orange.png`, color: colors.cream, qty: 12 }],
    nutrition: ["Orange"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist v62Z3v
    tasteVideos: [
      {
        caption: "Orange You Obsessed Yet?",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-cwxrgmzp/transcoded/720/quickorangevideo.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-cwxrgmzp/transcoded/720/quickorangevideo.jpg",
      },
      {
        caption: "you alwaysss gotta tap the top 💅",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-zmqldfse/transcoded/720/Orangepour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-zmqldfse/transcoded/720/Orangepour.jpg",
      },
      {
        caption: "Frozen Aperol Spritz with Orange Poppi!",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-ilutqjbk/transcoded/720/orangefrozenaperol.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239912-ilutqjbk/transcoded/720/orangefrozenaperol.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "AMAZING",
        text: "The perfect orange crisp drink that is not too sweet and tastes perfectly orangey.",
        author: "Anna",
      },
      {
        title: "My go-to",
        text: "Love all the flavors but orange is my fav. We always have a case in the house for when we want something fun to drink without too much sugar.",
        author: "Shelby",
      },
      {
        title: "My first flavor and THE BEST!!",
        text: "Orange was my first Poppi flavor and it is by far my favorite!! So delicious, refreshing, and my go-to whether it’s a tummy ache or I just need a summer-y drink in my hand. GO GRAB IT NOW!!",
        author: "taylorfayelor",
      },
      {
        title: "So good!",
        text: "I just recently started drinking Poppi within the last few months and now it is a sad day when I run out or forget to bring one to work. They are the best! The orange is my favorite flavor but so many others are great too :)",
        author: "LFitz",
      },
    ],
  },
  "cherry-cola": {
    handle: "cherry-cola",
    upc: "810063711054",
    caffeineBadge: true,
    name: "Cherry Cola",
    description:
      "When two faves maximize their joint slay, everybody wins. Lush, tart cherry collabs with crisp, refreshing cola for this juicy twist on a classic.",
    packSize: "12 Pack",
    primary: "#700058",
    secondary: "#fd303a",
    gallery: [
      `${A}/live/cherry-cola-01.webp`,
      `${A}/live/cherry-cola-02.webp`,
      `${A}/live/cherry-cola-03.webp`,
      `${A}/live/cherry-cola-04.webp`,
      `${A}/live/cherry-cola-05.webp`,
      `${A}/live/cherry-cola-06.webp`,
      `${G}/cherry-cola-07.jpg`,
      `${G}/cherry-cola-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/cherry-cola-01.webp`,
      `${A}/live/cherry-cola-02.webp`,
      [`${A}/live/cherry-cola-03.webp`, `${A}/live/cherry-cola-04.webp`],
      `${A}/live/cherry-cola-05.webp`,
      `${A}/live/cherry-cola-06.webp`,
      `${G}/cherry-cola-07.jpg`,
      `${G}/cherry-cola-08.jpg`,
    ],
    flavors: [{ name: "Cherry Cola", icon: `${A}/flavor-icons/cherry-cola.png`, color: "#fd303a", qty: 12 }],
    nutrition: ["Cherry Cola"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist oWlY4W
    tasteVideos: [
      {
        caption: "can't work without my thingsss",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-ltpyjgnf/transcoded/720/worksetup.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-ltpyjgnf/transcoded/720/worksetup.jpg",
      },
      {
        caption: "Giving Cherry Cola Her Moment",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-fsobqyan/transcoded/720/cherrycolaevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-fsobqyan/transcoded/720/cherrycolaevergreen.jpg",
      },
      {
        caption: "Can We Talk About Cherry Cola?",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-cisrjgea/transcoded/720/allisontalkingaboutCherryCola.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240390-cisrjgea/transcoded/720/allisontalkingaboutCherryCola.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "A fav!!!!",
        text: "Cherry Coke has been a favorite of mine since childhood. Was so excited when Poppi made a cherry cola flavor, and it did not disappoint!!! It’s soooooo good!!!!!!!",
        author: "BamaWhit",
      },
      {
        title: "Real Cherry-liscius",
        text: "If you love Cherry Limeade, you will love this one even more. I’m in Cherry-lust.",
        author: "MsTine19",
      },
      {
        title: "The Cherry Cola Poppi is a game changer",
        text: "So good - Cherry Coke has always been my favorite, and I have been waiting for Poppi to come through with this flavor. They nailed it! I will be drinking this instead of Cherry Coke from now on.",
        author: "RP24",
      },
      {
        title: "Love the cherry cola flavor!",
        text: "Cherry Cola is my new favorite Poppi flavor! It has just the right amount of cherry flavor and gives me the fizz I need to stay away from diet sodas.",
        author: "Samplegirl",
      },
    ],
  },
  "raspberry-rose": {
    handle: "raspberry-rose",
    upc: "709586514894",
    name: "Raspberry Rose",
    description:
      "Put your camera in 0.5x, because this super refreshing, oh-so-juicy, and lightly floral flavor belongs in your next photo dump. Slay!",
    packSize: "12 Pack",
    primary: colors.pink,
    secondary: colors.red,
    gallery: [
      `${A}/live/raspberry-rose-01.webp`,
      `${A}/live/raspberry-rose-02.webp`,
      `${A}/live/raspberry-rose-03.webp`,
      `${A}/live/raspberry-rose-04.webp`,
      `${A}/live/raspberry-rose-05.webp`,
      `${A}/live/raspberry-rose-06.webp`,
      `${G}/raspberry-rose-07.jpg`,
      `${G}/raspberry-rose-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/raspberry-rose-01.webp`,
      `${A}/live/raspberry-rose-02.webp`,
      [`${A}/live/raspberry-rose-03.webp`, `${A}/live/raspberry-rose-04.webp`],
      `${A}/live/raspberry-rose-05.webp`,
      `${A}/live/raspberry-rose-06.webp`,
      `${G}/raspberry-rose-07.jpg`,
      `${G}/raspberry-rose-08.jpg`,
    ],
    flavors: [{ name: "Raspberry Rose", icon: `${A}/flavor-icons/raspberry-rose.png`, color: colors.red, qty: 12 }],
    nutrition: ["Raspberry Rose"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist oj2VEo
    tasteVideos: [
      {
        caption: "berry-utiful <3",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fcjztnhb/transcoded/720/raspberryrose-evergreenrecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fcjztnhb/transcoded/720/raspberryrose-evergreenrecipe.jpg",
      },
      {
        caption: "Berry in Love with Raspberry Rose",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-flgwxcvo/transcoded/720/rasroseromanticvibe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-flgwxcvo/transcoded/720/rasroseromanticvibe.jpg",
      },
      {
        caption: "Raspberry Rose Sweet Treat",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fqukhleo/transcoded/720/rasroseicecream.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-fqukhleo/transcoded/720/rasroseicecream.jpg",
      },
      {
        caption: "ras rose our superstar ⭐️",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-puaglbtc/transcoded/720/allisontiktok.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239607-puaglbtc/transcoded/720/allisontiktok.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "THE BEST FLAVOR",
        text: "I have been drinking Poppi for two years and the Raspberry Rose is a staple in my house. It is a great mixer, mid-day pick me up, or amazing just on its own!",
        author: "efranze",
      },
      {
        title: "BEST POPPI FLAVOR HANDS DOWN",
        text: "My husband and I are addicted to the raspberry rose. We love to put it in a wine glass and pretend we are fancy :)",
        author: "MCCW",
      },
      {
        title: "A perfect treat!",
        text: "I bought raspberry rose when I was on vacation and selecting fun products from a local grocery store and I'm so glad I did!!!!! It's the perfect balance of fruit and floral. Every time I pop one open I know I'm in for a refreshing treat!",
        author: "penny24",
      },
    ],
  },
  "grape": {
    handle: "grape",
    upc: "810063710392",
    name: "Grape",
    description:
      "Remember your fave soda from childhood? We just gave it a majorrrr upgrade. Juicy, bold, and beyond refreshing, it’s grape soda…all grown up.",
    packSize: "12 Pack",
    primary: "#8725c3",
    secondary: colors.lime,
    gallery: [
      `${A}/live/grape-01.webp`,
      `${A}/live/grape-02.webp`,
      `${A}/live/grape-03.webp`,
      `${A}/live/grape-04.webp`,
      `${A}/live/grape-05.webp`,
      `${A}/live/grape-06.webp`,
      `${G}/grape-07.jpg`,
      `${G}/grape-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/grape-01.webp`,
      `${A}/live/grape-02.webp`,
      [`${A}/live/grape-03.webp`, `${A}/live/grape-04.webp`],
      `${A}/live/grape-05.webp`,
      `${A}/live/grape-06.webp`,
      `${G}/grape-07.jpg`,
      `${G}/grape-08.jpg`,
    ],
    flavors: [{ name: "Grape", icon: `${A}/flavor-icons/grape.png`, color: colors.lime, qty: 12 }],
    nutrition: ["Grape"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5aN92o
    tasteVideos: [
      {
        caption: "ur new fave Grape soda 🍇",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-yatwcsou/transcoded/720/grapepour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-yatwcsou/transcoded/720/grapepour.jpg",
      },
      {
        caption: "ASMR with poppi Grape",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-ibvanorm/transcoded/720/Grape-veryshortvideowithAllison.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-ibvanorm/transcoded/720/Grape-veryshortvideowithAllison.jpg",
      },
      {
        caption: "A moment with Grape",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-oqbazxsy/transcoded/720/aestheticgrape.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239826-oqbazxsy/transcoded/720/aestheticgrape.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Best Grape Soda Ever!!!",
        text: "Whenever I'm out shopping and the Grape flavor Poppi is on the shelf, I'm buying. The taste is so refreshing and delicious on its own or with food. My favorite soda of all time.",
        author: "kad21",
      },
      {
        title: "Definitely a Favorite",
        text: "Absolutely tastes like grape soda! Such a fun flavor that reminds me of childhood.",
        author: "Naomi",
      },
      {
        title: "Obsessed",
        text: "One of my top 3 favorite flavors. Obsessed. It's the perfect blend of grape without being too powerful.",
        author: "Bekka",
      },
    ],
  },
  "cherry-limeade": {
    handle: "cherry-limeade",
    upc: "810063710071",
    name: "Cherry Limeade",
    description:
      "Sweet, but with an edge. Are we describing you or the soda in your hand? That’s one secret we’ll never tell, xoxo.",
    packSize: "12 Pack",
    primary: "#d10119",
    secondary: colors.lime,
    gallery: [
      `${A}/live/cherry-limeade-01.webp`,
      `${A}/live/cherry-limeade-02.webp`,
      `${A}/live/cherry-limeade-03.webp`,
      `${A}/live/cherry-limeade-04.webp`,
      `${A}/live/cherry-limeade-05.webp`,
      `${A}/live/cherry-limeade-06.webp`,
      `${G}/cherry-limeade-07.jpg`,
      `${G}/cherry-limeade-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/cherry-limeade-01.webp`,
      `${A}/live/cherry-limeade-02.webp`,
      [`${A}/live/cherry-limeade-03.webp`, `${A}/live/cherry-limeade-04.webp`],
      `${A}/live/cherry-limeade-05.webp`,
      `${A}/live/cherry-limeade-06.webp`,
      `${G}/cherry-limeade-07.jpg`,
      `${G}/cherry-limeade-08.jpg`,
    ],
    flavors: [{ name: "Cherry Limeade", icon: `${A}/flavor-icons/cherry-limeade.png`, color: colors.lime, qty: 12 }],
    nutrition: ["Cherry Limeade"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist gX48rg
    tasteVideos: [
      {
        caption: "If Cherry Limeade Could Talk…",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-kirqntjp/transcoded/720/cherrylimeadetiktok.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-kirqntjp/transcoded/720/cherrylimeadetiktok.jpg",
      },
      {
        caption: "Cherry Limeade fancam",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-ezkfsovl/transcoded/720/cherrylimeadepour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-ezkfsovl/transcoded/720/cherrylimeadepour.jpg",
      },
      {
        caption: "she's flawless 🍒🍋‍🟩",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-yliebrzk/transcoded/720/aestheticcherrylimeadevideos.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240028-yliebrzk/transcoded/720/aestheticcherrylimeadevideos.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Poppi is THE best FUN drink!",
        text: "We love Poppi -- it's a refreshing and FUN drink. Cherry Limeade is our favorite flavor because it's so rich in taste and you feel good drinking it.",
        author: "WenZo",
      },
      {
        title: "Best Poppi Flavor!",
        text: "I am in LOVE with Poppi sodas, but I find the cherry limeade flavor to be simply irresistible! It has just the right amount of sweetness, bubbles, and cherry-lime combo in every sip. Worth every penny!",
        author: "jinxthechemist",
      },
      {
        title: "Perfectly refreshing",
        text: "Cherry Limeade is always the flavor I reach for when I need a crisp, refreshing drink! Perfectly sweet and slightly tart. One of the all-time best flavors.",
        author: "Grace29",
      },
    ],
  },
  "watermelon": {
    handle: "watermelon",
    upc: "709586514832",
    name: "Watermelon",
    description:
      "Ready to have your rind blown? This soda’s almosttt as juicy as you are. And yeah, we’re flirting with you… Is it working?",
    packSize: "12 Pack",
    primary: "#6bff4f",
    secondary: "#f61f6c",
    gallery: [
      `${A}/live/watermelon-01.webp`,
      `${A}/live/watermelon-02.webp`,
      `${A}/live/watermelon-03.webp`,
      `${A}/live/watermelon-04.webp`,
      `${A}/live/watermelon-05.webp`,
      `${A}/live/watermelon-06.webp`,
      `${G}/watermelon-07.jpg`,
      `${G}/watermelon-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/watermelon-01.webp`,
      `${A}/live/watermelon-02.webp`,
      [`${A}/live/watermelon-03.webp`, `${A}/live/watermelon-04.webp`],
      `${A}/live/watermelon-05.webp`,
      `${A}/live/watermelon-06.webp`,
      `${G}/watermelon-07.jpg`,
      `${G}/watermelon-08.jpg`,
    ],
    flavors: [{ name: "Watermelon", icon: `${A}/flavor-icons/watermelon.png`, color: "#f61f6c", qty: 12 }],
    nutrition: ["Watermelon"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist 5qK2Xg
    tasteVideos: [
      {
        caption: "the girls who get it, get it",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-botiyuzm/transcoded/720/watermelonpour.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-botiyuzm/transcoded/720/watermelonpour.jpg",
      },
      {
        caption: "Satisfying Watermelon Pour",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-mcziqsuv/transcoded/720/watermelonpour2.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-mcziqsuv/transcoded/720/watermelonpour2.jpg",
      },
      {
        caption: "watermelon without the seeds 🍉",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-kmrqenzp/transcoded/720/watermelonevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749239730-kmrqenzp/transcoded/720/watermelonevergreen.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Watermelon Whamm!!",
        text: "Love this flavor its a burst of watermelon with a splash of lime- its the best flavor by far! It goes with any meal and great on its own!",
        author: "dreview1",
      },
      {
        title: "Best Watermelon Soda Everrrrrr",
        text: "I am obsessed with Poppi! When Watermelon came along I was BLOWN away!! Nice crisp and refreshing the perfect summer drink!",
        author: "LivyLove",
      },
      {
        title: "BEST FLAVOR",
        text: "I'm a big fan of every Poppi I've tried (which is most by now) but the watermelon is by far the BEST!",
        author: "Meg1738",
      },
    ],
  },
  "classic-cola": {
    handle: "classic-cola",
    upc: "810063710293",
    caffeineBadge: true,
    name: "Classic Cola",
    description:
      "You’re already a legend in your own mind, so why not sip something that matches your vibe? Have it all… (and then have some more).",
    packSize: "12 Pack",
    primary: "#e10600",
    secondary: "#ffd4c9",
    gallery: [
      `${A}/live/classic-cola-01.webp`,
      `${A}/live/classic-cola-02.webp`,
      `${A}/live/classic-cola-03.webp`,
      `${A}/live/classic-cola-04.webp`,
      `${A}/live/classic-cola-05.webp`,
      `${A}/live/classic-cola-06.webp`,
      `${G}/classic-cola-07.jpg`,
      `${G}/classic-cola-08.jpg`,
    ],
    galleryStrip: [
      `${A}/live/classic-cola-01.webp`,
      `${A}/live/classic-cola-02.webp`,
      [`${A}/live/classic-cola-03.webp`, `${A}/live/classic-cola-04.webp`],
      `${A}/live/classic-cola-05.webp`,
      `${A}/live/classic-cola-06.webp`,
      `${G}/classic-cola-07.jpg`,
      `${G}/classic-cola-08.jpg`,
    ],
    flavors: [{ name: "Classic Cola", icon: `${A}/flavor-icons/classic-cola.png`, color: "#ffd4c9", qty: 12 }],
    nutrition: ["Classic Cola"].map(nutritionFor),
    // live "Taste the Obsession" Firework playlist vGNy4g
    tasteVideos: [
      {
        caption: "ok but have you tried it with lime? 🍋‍🟩",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-gbiqzpvy/transcoded/720/ClassicColaRecipe.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-gbiqzpvy/transcoded/720/ClassicColaRecipe.jpg",
      },
      {
        caption: "Your New Classic Crush",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-ulgdtzfc/transcoded/720/classiccola-quickevergreen.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-ulgdtzfc/transcoded/720/classiccola-quickevergreen.jpg",
      },
      {
        caption: "You Can’t Beat This Classic",
        src: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-ipobkmlv/transcoded/720/Allisontalkingaboutclassiccola.mp4",
        thumb: "https://cdn4.fireworktv.com/medias/2025/6/6/1749240190-ipobkmlv/transcoded/720/Allisontalkingaboutclassiccola.jpg",
      },
    ],
    // live reviews, verbatim (server-rendered on drinkpoppi.com)
    reviews: [
      {
        title: "Poppi cola",
        text: "Love these as my cheat instead of a real cola - less sugar and taste is great!",
        author: "Aday789",
      },
      {
        title: "Classic Cola",
        text: "When I bought this, it was the best thing I ever tried.",
        author: "RieRie",
      },
      {
        title: "Better than a COKE!!",
        text: "I bought this about a week ago, and it is SO GOOD!! Brought it to replace Coke, and this has been the best Poppi flavor yet!!",
        author: "Lisapaola",
      },
      {
        title: "One of my favorite",
        text: "I’ve been buying this flavor along with several other flavors, and this one is truly a favorite. It gives you the taste of Coke!! Thank you for making this product!!",
        author: "Mickey",
      },
    ],
  },
};

const PDP_PRODUCTS_BY_HANDLE = new Map(
  Object.values(PDP_PRODUCTS).map(
    (product) => [product.handle, product] as const
  )
);

export function getPdpProductByHandle(handle: unknown): PdpProduct | undefined {
  if (typeof handle !== "string") return undefined;
  return PDP_PRODUCTS_BY_HANDLE.get(handle);
}

export const PDP_HANDLES = Object.keys(PDP_PRODUCTS);
